/**
 * PowerKick Her — Landing publique premium (refonte design 2026).
 * Route publique : /powerkick-her
 * Programme d'empowerment féminin par le kickboxing. Mascotte : MEI.
 * Design : Oswald (titres) + Manrope (corps) · Obsidian #0A0A0B · Rose #E11D48 · Or #FBBF24.
 */
import { useEffect, useState } from "react";
import { Sparkles, Flame, Users, Target, Check, ChevronRight, ShieldCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import MascotBand from "../components/MascotBand";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

const IMG = {
  hero: "https://images.unsplash.com/photo-1648634362534-238cb091708b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHx3b21lbiUyMGtpY2tib3hpbmclMjB0cmFpbmluZyUyMGRhcmt8ZW58MHx8fHwxNzgyMjc1NjMyfDA&ixlib=rb-4.1.0&q=85",
  pillarPower: "https://images.unsplash.com/photo-1590556409324-aa1d726e5c3c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHx3b21lbiUyMGtpY2tib3hpbmclMjB0cmFpbmluZyUyMGRhcmt8ZW58MHx8fHwxNzgyMjc1NjMyfDA&ixlib=rb-4.1.0&q=85",
  crm: "https://images.unsplash.com/photo-1539794830467-1f1755804d13?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHx3b21lbiUyMGZpdG5lc3MlMjBlbXBvd2VybWVudCUyMGRhcmt8ZW58MHx8fHwxNzgyMjc1NjMyfDA&ixlib=rb-4.1.0&q=85",
};

const PILLARS = [
  { icon: Flame, title: "Puissance & confiance", body: "Frappe avec force, précision, technique. Reprends ta puissance physique et repars la tête haute, invincible face aux défis du quotidien.", big: true },
  { icon: Sparkles, title: "Exutoire & gestion du stress", body: "Charge mentale immense ? Frappe dans les coussins, enchaîne les combinaisons. Vide ton sac dans un environnement sain et sécuritaire." },
  { icon: Users, title: "Communauté sans jugement", body: "100% féminin. Pas d'intimidation. Camaraderie, soutien mutuel, énergie collective contagieuse qui te ramène semaine après semaine." },
  { icon: Target, title: "Structure & résultats", body: "Programmes structurés sur 12 semaines. Endurance qui explose, tonus qui s'améliore, agilité qui se développe. Constance + discipline = résultats tangibles." },
];

const OSW = "font-['Oswald']";
const ROSE = "#E11D48";

export default function PowerKickHerLanding() {
  const [accessInfo, setAccessInfo] = useState(null);
  const [email, setEmail] = useState("");
  const [checking, setChecking] = useState(false);
  const [evalForm, setEvalForm] = useState({ full_name: "", email: "", phone: "", age: "", objectives: "" });
  const [submitting, setSubmitting] = useState(false);
  const [packs, setPacks] = useState([]);
  const [launchOffer, setLaunchOffer] = useState(null);
  const [groupSession, setGroupSession] = useState(null);
  const [promo, setPromo] = useState("");
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    document.title = "PowerKick Her — Empowerment féminin par le kickboxing";
    try {
      // Promotions retirées : on ignore tout paramètre ?promo= (aucun rabais affiché).
      setPromo("");
    } catch { /* noop */ }
    (async () => {
      try {
        const r = await axios.get(`${API}/powerkick-her/video-packs`);
        setPacks(r.data.packs || []);
        setLaunchOffer(r.data.launch_offer);
        setGroupSession(r.data.group_session || null);
      } catch { /* silencieux */ }
      try {
        const t = await axios.get(`${API}/powerkick-her/public/testimonials`);
        setTestimonials(t.data.testimonials || []);
      } catch { /* silencieux */ }
    })();
  }, []);

  const checkAccess = async () => {    if (!email.trim()) return toast.error("Entre ton courriel d'abord");
    setChecking(true);
    try {
      const r = await axios.post(`${API}/powerkick-her/access-check`, { email: email.trim() });
      setAccessInfo(r.data);
      if (r.data.is_member) toast.success("🥋 Membre confirmée ! Accès gratuit.");
    } catch { toast.error("Erreur — réessaie"); }
    setChecking(false);
  };

  const submitEval = async (e) => {
    e.preventDefault();
    if (!evalForm.full_name || !evalForm.email) return toast.error("Nom + courriel requis");
    setSubmitting(true);
    try {
      await axios.post(`${API}/powerkick-her/private-eval-request`, {
        ...evalForm, age: evalForm.age ? parseInt(evalForm.age, 10) : undefined,
        promo_code: promo || undefined,
      });
      toast.success("Demande envoyée ! On te contacte sous 48h.");
      setEvalForm({ full_name: "", email: "", phone: "", age: "", objectives: "" });
    } catch { toast.error("Erreur — réessaie"); }
    setSubmitting(false);
  };

  const inputCls = "w-full bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-3 text-sm text-zinc-50 placeholder:text-zinc-500 focus:border-rose-600 focus:ring-1 focus:ring-rose-600/40 outline-none transition-colors";

  const buy = async (kind, useLaunch = false) => {
    if (!email.trim()) return toast.error("Entre ton courriel d'abord");
    try {
      const r = await axios.post(`${API}/powerkick-her/checkout`, {
        kind, full_name: email.split("@")[0], email: email.trim(), use_launch_offer: useLaunch,
      });
      if (r.data?.checkout_url) { window.location.href = r.data.checkout_url; }
      else toast.error("Erreur de paiement");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur — réessaie"); }
  };

  return (
    <div className={`min-h-screen bg-[#0A0A0B] text-zinc-100 font-['Manrope'] selection:bg-rose-600 selection:text-white`} data-testid="powerkick-her-landing">

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.hero} alt="" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-12 items-center">
            <div>
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-rose-500/40 text-rose-200 text-xs uppercase tracking-[0.2em] mb-7 backdrop-blur ${OSW}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" /> 100% femmes · 12 semaines · avec MEI
              </div>
              <div className="flex items-center gap-5">
                <img src="/logos/ph02_sm.webp" alt="PowerKick Her" className="w-20 h-20 sm:w-28 sm:h-28 object-contain rounded-full bg-white/95 p-1 shadow-lg shadow-rose-900/40 shrink-0" data-testid="pkh-emblem" />
                <h1 className={`${OSW} uppercase text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95] tracking-tight text-white`}>
                  Power<span className="text-rose-500">Kick</span>Her
                </h1>
              </div>
              <p className="text-xl sm:text-2xl text-zinc-200 mt-4 max-w-2xl font-semibold">
                Reprends le contrôle de ton corps, ton énergie, ton bien-être.
              </p>
              <p className="text-base text-zinc-400 mt-4 max-w-xl leading-relaxed">
                Kickboxing + entraînement fonctionnel dans un espace 100% féminin, sans jugement.
                Force physique, confiance, exutoire. Tu repars la tête haute, semaine après semaine.
              </p>

              <div className="mt-9 flex flex-wrap gap-3">
                <a href="#evaluation" data-testid="cta-eval"
                  className={`${OSW} uppercase tracking-wider px-7 py-3.5 rounded-full bg-rose-600 hover:bg-rose-700 text-white font-semibold text-sm inline-flex items-center gap-2 transition-all hover:-translate-y-0.5 shadow-lg shadow-rose-600/30`}>
                  Réserver mon cours d'essai (19,98 $) <ChevronRight className="w-4 h-4" />
                </a>
                <a href="#pricing" data-testid="cta-pricing"
                  className={`${OSW} uppercase tracking-wider px-7 py-3.5 rounded-full border border-white/25 hover:border-rose-500/60 hover:bg-white/5 text-white text-sm inline-flex items-center gap-2 transition-colors backdrop-blur`}>
                  Voir les forfaits
                </a>
                <a href="/powerkick-her/carnet" data-testid="cta-carnet"
                  className={`${OSW} uppercase tracking-wider px-7 py-3.5 rounded-full border border-rose-500/40 hover:bg-rose-500/10 text-rose-200 text-sm inline-flex items-center gap-2 transition-colors backdrop-blur`}>
                  Voir le carnet de techniques
                </a>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-5 text-xs text-zinc-400">
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-rose-400" /> Espace 100% sécuritaire</span>
                <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4 text-rose-400" /> Communauté bienveillante</span>
                <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-rose-400" /> Aucun engagement</span>
              </div>
            </div>

            {/* MASCOTTE MEI */}
            <div className="hidden lg:flex justify-center items-center" data-testid="mascot-mei-hero">
              <div className="relative">
                <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-rose-600/40 to-amber-500/10 blur-3xl" />
                <img src={`${API}/marketing-assets/file/asset_mascot_mei.jpg`} alt="MEI — Mascotte PowerKick Her"
                  className="relative w-[320px] rounded-[1.75rem] border border-rose-500/40 shadow-2xl shadow-rose-600/30" />
                <div className={`${OSW} absolute -bottom-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-rose-600 text-white text-xs font-bold tracking-[0.3em] uppercase`}>MEI</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4 PILIERS — bento */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="mb-10">
          <span className={`${OSW} uppercase tracking-[0.25em] text-rose-500 text-sm`}>Pourquoi PowerKick Her</span>
          <h2 className={`${OSW} uppercase text-3xl sm:text-4xl font-bold text-white mt-2`}>Ce que tu viens chercher ici</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {PILLARS.map((p, i) => (
            <div key={p.title} data-testid={`pillar-${i}`}
              className={`group relative overflow-hidden rounded-2xl border border-zinc-800 p-7 transition-all hover:border-rose-600/50 hover:-translate-y-1 ${p.big ? "md:row-span-2 md:flex md:flex-col md:justify-end min-h-[280px]" : "bg-[#18181B]"}`}>
              {p.big && (
                <>
                  <img src={IMG.pillarPower} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-60 group-hover:scale-105 transition-all duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-[#0A0A0B]/80 to-transparent" />
                </>
              )}
              <div className="relative">
                <span className="inline-flex w-11 h-11 items-center justify-center rounded-xl bg-rose-600/15 border border-rose-600/30">
                  <p.icon className="w-5 h-5 text-rose-400" />
                </span>
                <h3 className={`${OSW} uppercase text-xl font-semibold text-white mt-4 tracking-wide`}>{p.title}</h3>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed max-w-md">{p.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* MASCOTTE MEI band */}
      <div className="py-10 border-t border-zinc-900">
        <MascotBand order={["MEI", "LANA", "KAI"]}
          title="MEI, ton ambassadrice PowerKick Her"
          subtitle="MEI incarne le kickboxing au féminin : puissance, grâce et confiance. Avec KAI et LANA, toute la communauté KAIZEN t'accompagne." />
      </div>

      {/* TÉMOIGNAGES — avis réels des participantes (sondage fin de session) */}
      {testimonials.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 py-20 border-t border-zinc-900" data-testid="pkh-testimonials">
          <div className="mb-8 text-center">
            <span className={`${OSW} uppercase tracking-[0.25em] text-rose-500 text-sm`}>Elles l'ont vécu</span>
            <h2 className={`${OSW} uppercase text-3xl sm:text-4xl font-bold text-white mt-2`}>Ce qu'en disent nos guerrières</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <div key={i} data-testid={`pkh-testimonial-${i}`} className="rounded-2xl border border-zinc-800 bg-[#141416] p-6 flex flex-col">
                <div className="flex gap-0.5 mb-3">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <span key={n} className={n <= (t.rating || 5) ? "text-[#FBBF24]" : "text-zinc-700"}>★</span>
                  ))}
                </div>
                <p className="text-zinc-200 text-sm leading-relaxed flex-1">"{t.testimonial}"</p>
                <div className="mt-4 text-xs text-rose-400 font-semibold uppercase tracking-wide">— {t.name}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* PRICING / ACCÈS MEMBRE */}
      <section id="pricing" className="max-w-3xl mx-auto px-6 py-20 border-t border-zinc-900">
        <div className="mb-6">
          <span className={`${OSW} uppercase tracking-[0.25em] text-rose-500 text-sm`}>Accès & forfaits</span>
          <h2 className={`${OSW} uppercase text-3xl font-bold text-white mt-2`}>Tu es déjà membre du dojo ?</h2>
          <p className="text-sm text-zinc-400 mt-2">Vérifie ton accès gratuit en entrant ton courriel.</p>
        </div>

        <div className="flex gap-2 max-w-md">
          <input type="email" placeholder="ton@courriel.com" value={email}
            onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="access-email-input" />
          <button onClick={checkAccess} disabled={checking} data-testid="check-access-btn"
            className={`${OSW} uppercase tracking-wider px-6 py-3 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white font-semibold text-sm transition-colors whitespace-nowrap`}>
            {checking ? "..." : "Vérifier"}
          </button>
        </div>

        {accessInfo && (
          <div className={`mt-6 rounded-2xl border p-5 ${accessInfo.is_member ? "bg-emerald-500/10 border-emerald-500/40" : "bg-rose-600/10 border-rose-500/40"}`} data-testid="access-result">
            {accessInfo.is_member ? (
              <>
                <div className="flex items-center gap-2 text-emerald-300 font-semibold"><Check className="w-5 h-5" /> Accès gratuit confirmé</div>
                <p className="text-sm text-emerald-100/80 mt-2">{accessInfo.message}</p>
                <a href="/app/avatar-studio" className="inline-flex items-center gap-1 mt-3 text-sm text-emerald-300 font-semibold hover:gap-2 transition-all">Accéder à la bibliothèque vidéo <ArrowRight className="w-4 h-4" /></a>
              </>
            ) : (
              <>
                <p className="text-sm text-zinc-200">{accessInfo.pricing.launch_offer_available ? "🎉 Tu es éligible à l'offre de lancement !" : "Voici tes options :"}</p>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-xl bg-[#0A0A0B] border border-rose-500/40 p-4 relative">
                    {accessInfo.pricing.launch_offer_available && <div className={`${OSW} absolute -top-2.5 left-4 px-2 py-0.5 rounded-full bg-amber-400 text-zinc-950 text-[10px] font-bold uppercase tracking-wider`}>Lancement</div>}
                    <div className={`${OSW} text-xs uppercase tracking-widest text-rose-400`}>Pack vidéo</div>
                    <div className={`${OSW} text-3xl font-bold text-white mt-1`}>{accessInfo.pricing.launch_offer_available ? `${accessInfo.pricing.launch_offer_price_cad.toFixed(2)} $` : `${accessInfo.pricing.pack_price_cad.toFixed(2)} $`}</div>
                    {accessInfo.pricing.launch_offer_available && <div className="text-xs text-amber-300 mt-1">au lieu de {accessInfo.pricing.pack_price_cad.toFixed(2)} $ · {accessInfo.pricing.launch_offer_duration_months} mois</div>}
                    <button onClick={() => buy("video_pack", accessInfo.pricing.launch_offer_available)} data-testid="buy-video-pack-btn"
                      className={`${OSW} uppercase tracking-wider mt-3 w-full px-4 py-2.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-semibold text-xs transition-colors`}>
                      Payer le pack vidéo
                    </button>
                  </div>
                  <div className="rounded-xl bg-[#0A0A0B] border border-zinc-800 p-4">
                    <div className={`${OSW} text-xs uppercase tracking-widest text-rose-400`}>Évaluation privée</div>
                    <div className={`${OSW} text-2xl font-bold text-white mt-1`}>19,98 $ + tx</div>
                    <a href="#evaluation" className="text-xs text-rose-300 font-semibold hover:underline">Réserver →</a>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {groupSession && (
          <div className="mt-12 rounded-2xl border border-rose-500/40 bg-gradient-to-b from-rose-600/10 to-[#18181B] p-6" data-testid="group-session-block">
            <div className={`${OSW} text-xs uppercase tracking-widest text-rose-400`}>Cours de groupe · en présentiel</div>
            <h3 className={`${OSW} uppercase text-2xl font-bold text-white mt-1 tracking-wide`}>{groupSession.label}</h3>
            <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
              {groupSession.class_price_cad.toFixed(0)} $ / cours · {groupSession.sessions_per_week} cours par semaine · {groupSession.weeks} semaines
              <span className="text-zinc-500"> ({groupSession.total_classes} cours au total)</span>
            </p>
            <div className="flex flex-wrap items-end gap-4 mt-4">
              <div className={`${OSW} text-3xl font-bold text-rose-300`}>{groupSession.price_cad.toFixed(2)} $</div>
              <button onClick={() => buy("group_session")} data-testid="buy-group-session-btn"
                className={`${OSW} uppercase tracking-wider px-6 py-3 rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-semibold text-sm transition-colors`}>
                S'inscrire et payer la session
              </button>
            </div>
            <p className="text-[11px] text-zinc-500 mt-3">Entre ton courriel dans le champ « Vérifier » ci-dessus, puis paie en ligne de façon sécurisée.</p>
          </div>
        )}

        {packs.length > 0 && (
          <div className="mt-12">
            <h3 className={`${OSW} uppercase text-lg font-semibold text-white mb-4 tracking-wide`}>Forfaits vidéo disponibles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {packs.map((p, idx) => (
                <div key={p.id} data-testid={`pack-${p.id}`}
                  className={`relative rounded-2xl border p-5 transition-all hover:-translate-y-1 ${idx === 1 ? "border-amber-400/50 bg-gradient-to-b from-amber-400/5 to-[#18181B] shadow-lg shadow-amber-500/10" : "border-zinc-800 bg-[#18181B] hover:border-rose-600/40"}`}>
                  {idx === 1 && <div className={`${OSW} absolute -top-2.5 left-5 px-2.5 py-0.5 rounded-full bg-amber-400 text-zinc-950 text-[10px] font-bold uppercase tracking-wider`}>Populaire</div>}
                  <div className={`${OSW} uppercase text-lg font-semibold text-white tracking-wide`}>{p.title}</div>
                  <div className="text-xs text-zinc-500 mt-1">{p.duration_weeks} semaines · {p.video_count} vidéos</div>
                  <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{p.description}</p>
                  <div className={`${OSW} mt-4 text-2xl font-bold ${idx === 1 ? "text-amber-300" : "text-rose-400"}`}>{p.price_cad.toFixed(2)} $</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* CRM EVAL — split layout */}
      <section id="evaluation" className="border-t border-zinc-900">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2">
          <div className="relative min-h-[320px] lg:min-h-full overflow-hidden">
            <img src={IMG.crm} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-black/60 to-black/30 lg:bg-gradient-to-r" />
            <div className="relative p-8 lg:p-12 h-full flex flex-col justify-end">
              <h2 className={`${OSW} uppercase text-3xl sm:text-4xl font-bold text-white leading-tight`}>Prête à franchir<br />la première porte ?</h2>
              <p className="text-zinc-300 mt-3 max-w-md">Un premier cours d'essai à 19,98 $ (+ taxes), sans engagement. On valide tes objectifs et ton point de départ idéal.</p>
            </div>
          </div>

          <div className="p-8 lg:p-12 bg-[#0A0A0B]">
            <span className={`${OSW} uppercase tracking-[0.25em] text-rose-500 text-sm`}>Cours d'essai 19,98 $</span>
            <h3 className={`${OSW} uppercase text-2xl font-bold text-white mt-2 mb-6`}>Réserve ta place</h3>
            {promo && (
              <div className="mb-5 rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3" data-testid="promo-banner">
                <p className="text-rose-300 text-sm font-semibold">🎟️ Offre affiche débloquée — <span className="text-white">1ᵉʳ mois à -50%</span></p>
                <p className="text-zinc-400 text-xs mt-1">Code <span className="font-mono text-rose-300">{promo}</span> appliqué à ta demande.</p>
              </div>
            )}
            <form onSubmit={submitEval} className="space-y-3" data-testid="eval-form">
              <input required placeholder="Nom complet *" value={evalForm.full_name}
                onChange={(e) => setEvalForm({ ...evalForm, full_name: e.target.value })} className={inputCls} data-testid="eval-name" />
              <input required type="email" placeholder="Courriel *" value={evalForm.email}
                onChange={(e) => setEvalForm({ ...evalForm, email: e.target.value })} className={inputCls} data-testid="eval-email" />
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Téléphone" value={evalForm.phone}
                  onChange={(e) => setEvalForm({ ...evalForm, phone: e.target.value })} className={inputCls} data-testid="eval-phone" />
                <input type="number" placeholder="Âge" min="18" max="80" value={evalForm.age}
                  onChange={(e) => setEvalForm({ ...evalForm, age: e.target.value })} className={inputCls} data-testid="eval-age" />
              </div>
              <textarea placeholder="Pourquoi PowerKick Her t'attire ? (optionnel)" value={evalForm.objectives}
                onChange={(e) => setEvalForm({ ...evalForm, objectives: e.target.value })} className={`${inputCls} min-h-[110px]`} data-testid="eval-objectives" />
              <button type="submit" disabled={submitting} data-testid="submit-eval-btn"
                className={`${OSW} uppercase tracking-wider w-full px-5 py-3.5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white font-semibold text-sm inline-flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 shadow-lg shadow-rose-600/30`}>
                {submitting ? "Envoi..." : <>Envoyer ma demande <ArrowRight className="w-4 h-4" /></>}
              </button>
              <p className="text-xs text-zinc-500 text-center">Réponse sous 48h · aucune carte requise</p>
            </form>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-900 py-8 text-center text-xs text-zinc-600">
        <span className={OSW}>PowerKickHer</span> · Programme du Dojo Kru Ralph · Val-d&apos;Or
      </footer>
    </div>
  );
}
